<template>
    <v-container fluid>
        <fl-filter v-if="!showPickupSelector" @applyFilters="applyFilters"></fl-filter>
        <v-card v-if="showPickupSelector">
            <v-card-title class="secondary white--text text-center">
                Select Pickup
            </v-card-title>
            <v-card-text>
                <v-card class="mt-4 mb-4 pb-2" v-show="!needPickupLocation">
                    <v-card-title>Proximity</v-card-title>
                    <v-card-text>
                        <v-form>
                            <v-btn-toggle
                                    v-model="proximityMode"
                                    tile
                                    color="secondary darken-3"
                                    group
                                    dense
                                    mandatory
                            >
                                <v-btn value="miles" text :disabled="!proximityFilterEditable" class="ml-0">
                                    Miles
                                </v-btn>

                                <v-btn value="cityState" text :disabled="!proximityFilterEditable">
                                    City & State
                                </v-btn>

                                <v-btn value="zip" text :disabled="!proximityFilterEditable">
                                    Zip Code
                                </v-btn>
                            </v-btn-toggle>

                            <div v-if="proximityMode === 'miles'">
                                <v-select :items="mileOptions"
                                          item-text="label"
                                          item-value="val"
                                          label="Max Distance"
                                          placeholder="Max Distance"
                                          v-model="miles"
                                          :disabled="!proximityFilterEditable"
                                          hide-details
                                ></v-select>
                            </div>

                            <div v-if="proximityMode === 'cityState'">
                                <v-text-field
                                        label="City"
                                        required
                                        v-model="city"
                                        :disabled="!proximityFilterEditable"
                                ></v-text-field>

                                <v-select :items="statesOptions"
                                          item-text="name"
                                          item-value="code"
                                          label="State"
                                          placeholder="State"
                                          v-model="state"
                                          :disabled="!proximityFilterEditable"
                                          hide-details
                                ></v-select>
                            </div>

                            <div v-if="proximityMode === 'zip'">
                                <v-text-field
                                        label="Zip Code"
                                        required
                                        v-model="zipCode"
                                        :disabled="!proximityFilterEditable"
                                        hide-details
                                ></v-text-field>
                            </div>
                        </v-form>
                    </v-card-text>
                    <v-card-actions class="ml-2">
                        <v-btn @click="editProximityFilters" v-show="!proximityFilterEditable" small>Change</v-btn>
                        <v-btn @click="applyProximityFilters" v-show="proximityFilterEditable" small>Apply</v-btn>
                    </v-card-actions>
                </v-card>

                <v-select
                        :items="pickupLocations"
                        item-text="name"
                        item-value="id"
                        label="Location"
                        placeholder="Location"
                        v-show="needPickupLocation"
                        v-model="pickupLocation"
                        class="pt-10"
                ></v-select>

                <v-select
                        :items="pickupDates"
                        item-text="userDate"
                        item-value="gmtDate"
                        label="Date"
                        placeholder="Date"
                        v-model="pickupDate"
                        v-show="showDateInput"
                        :disabled="!pickupDates"
                ></v-select>
                <v-select :items="pickupTimes"
                          item-text="userTime"
                          item-value="timeValue"
                          label="Time"
                          placeholder="Time"
                          v-model="pickupTime"
                          v-show="showTimeInput"
                          :disabled="!(pickupDate && pickupTimes)"
                ></v-select>

                <v-btn v-show="pickupTime" @click="locationSelector">Search</v-btn>
            </v-card-text>
        </v-card>

        <v-card v-if="locations && locations.length==0 && !showPickupSelector">
            <v-card-title class="secondary white--text text-center">
                No Locations
            </v-card-title>
            <v-card-text>
                <p>We're sorry, but no locations are available at this time.</p>
                <v-btn @click="startOver" class='ma-3'>Start Over</v-btn>
                <v-btn @click="$store.dispatch('toggleFilter')" class='ma-3'>
                    <v-icon>mdi-filter</v-icon>
                    Update Filter
                </v-btn>
            </v-card-text>
        </v-card>
        <div v-if="!showPickupSelector">
            <v-card v-if="locations && locations.length>0">
                <v-card-title class="secondary white--text text-center">
                    <div>Please Choose a {{pickupLocation ? "Vendor" : "Location"}}</div>
                </v-card-title>
            </v-card>

            <v-btn @click="$store.dispatch('toggleFilter')" class="ma-3" small v-if="locations && locations.length > 0">
                <v-icon>mdi-filter</v-icon>
                Filter
            </v-btn>

            <v-row v-if="locations && locations.length>0">
                <v-col :cols="columnCount"
                       :key="l.id"
                       v-for="l in locations"
                >
                    <v-card>
                        <v-card-text class="text-center">
                            <v-chip class="ma-2 float-md-right"
                                    color="success"
                                    label
                                    small
                                    v-if="l.distance && !needPickupLocation"
                            >
                                {{l.distance}} {{distanceLabel(l.distance)}}
                            </v-chip>

                            <div :style="{backgroundImage: 'url(\'' + l.distributorLogo + '\')'}"
                                 class="logo-container mt-6">

                            </div>
                            <v-divider></v-divider>
                            <div>
                                <v-spacer></v-spacer>
                                <v-btn :disabled="!l.biography" @click="moreInfo(l)" class="ma-4" small>
                                    <v-icon dark left>mdi-information-variant</v-icon>
                                    More Info
                                </v-btn>
                                <v-btn @click="selectLocation(l.distributorId)" class="ma-4" color="secondary"
                                       small>
                                    <v-icon dark left>mdi-check-circle</v-icon>
                                    Select
                                </v-btn>
                            </div>

                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <v-dialog max-width="290" persistent v-model="errorDialog">
            <v-card>
                <v-card-title class="error white--text">Error</v-card-title>
                <v-card-text>{{errorText}}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="errorDialog = false" color="green darken-1" text>OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-if="currentDistributor"
                  v-model="moreInfoModal">

            <v-card>
                <v-card-title class="headline grey lighten-2"
                              primary-title>
                    {{currentDistributor.distributorName}}
                </v-card-title>
                <v-card-text class="mt-4" v-html="currentDistributor.biography">
                </v-card-text>
                <v-divider></v-divider>
                <v-card-text class="mt-4 text-center">
                    <div>{{currentDistributor.address1}}</div>
                    <div v-if="currentDistributor.address2">{{currentDistributor.address2}}</div>
                    <div>{{currentDistributor.city}}, {{currentDistributor.state}} {{currentDistributor.zip}}</div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="moreInfoModal=false" small>
                        <v-icon dark left>mdi-check-circle</v-icon>
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
    import api from "../services/aggrega8-api";
    import moment from 'moment';
    import flFilter from "./Filter";
    import StatesService from '../services/states-service';

    class BaseLocationFlow {
        constructor(component) {
            this.component = component;
        }

        applyProximityFilters() {}

        locationChanged() {
            this.component.showDateInput = false;
            this.component.pickupDates = [];
            this.component.pickupDate = null;
            this.component.showTimeInput = false;
            this.component.pickupTimes = [];
            this.component.pickupTime = null;
        }

        dateChanged(val) {
            if(!val)
                return;

            this.component.pickupTimes = [];
            this.component.pickupTime = null;
            this.component.showTimeInput = false;
            this.component.$store.dispatch("setExecuting", true);

            api.get('/consumer/filterTimes', {params: this.component.buildCommonQueryParams()})
                .then(
                    (results) => {
                        this.component.pickupTimes = results.data;
                        this.component.showTimeInput = true;
                    },
                    (error) => {
                        this.component.errorText = error.response.data;
                        this.component.errorDialog = true;
                    }
                ).then(() => {
                    this.component.$store.dispatch("setExecuting", false);
                });
        }
    }

    class ParentPickupLocationFlow extends BaseLocationFlow {
        constructor(component) {
            super(component);
        }

        applyProximityFilters() {
            super.applyProximityFilters();
            this._loadPickupLocations();
        }

        _loadPickupLocations() {
            this.component.loadPickupLocations()
                .then(
                    (results) => {
                        this.component.pickupLocations = results.data;
                        this.component.needPickupLocation = true;
                    },
                    (error) => {
                        this.component.errorText = error.response.data;
                        this.component.errorDialog = true;
                    }
                ).then(() => {
                    this.component.$store.dispatch("setExecuting", false);
                });
        }

        locationChanged(val) {
            super.locationChanged(val);

            if(val)
                this.component.getPickupDates();
        }

        dateChanged(val) {
            if(this.component.pickupLocation)
                return super.dateChanged(val);
        }
    }

    class ChildrenPickupLocationFlow extends BaseLocationFlow {
        constructor(component) {
            super(component);

            this.component.getPickupDates()
        }

        applyProximityFilters() {
            super.applyProximityFilters();
            this.component.getPickupDates();
        }
    }

    export default {
        name: 'Locations',
        components: {
            flFilter
        },
        computed: {
            apiKey: function () {
                return this.$store.state.apiKey
            },
            filters: function () {
                return this.$store.state.filters
            },
            userDetails: function () {
                return this.$store.state.userDetails
            },
            loggedIn: function () {
                return this.$store.state.loggedIn
            },
            executing: function () {
                return this.$store.state.executing
            },
            selectedProgram: function () {
                return this.$store.state.selectedProgram
            },
            columnCount: function () {
                let cols = 12;
                switch (this.$vuetify.breakpoint.name) {
                    case 'xs': {
                        cols = 6
                        break;
                    }
                    case 'sm': {
                        cols = 6
                        break;
                    }
                    case 'md': {
                        cols = 4
                        break;
                    }
                    case 'lg': {
                        cols = 3
                        break;
                    }
                    case 'xl': {
                        cols = 2
                        break;
                    }
                }
                return cols
            },
            pickupLocation: {
                get() {
                    return this.$store.state.pickupFilters.pickupLocation;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_LOCATION', val);
                }
            },
            vendor: {
                get() {
                    return this.$store.state.pickupFilters.vendor;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_VENDOR', val);
                }
            },
            pickupLocations: {
                get() {
                    return this.$store.state.pickupFilters.pickupLocationOptions;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_LOCATION_OPTIONS', val);
                }
            },
            proximityMode: {
                get() {
                    return this.$store.state.pickupFilters.proximityMode;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_PROXIMITY_MODE', val);
                }
            },
            proximityOptions: {
                get() {
                    return this.$store.state.pickupFilters.proximityOptions;
                }
            },
            miles: {
                get() {
                    return this.$store.state.pickupFilters.miles;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_MILES', val);
                }
            },
            city: {
                get() {
                    return this.$store.state.pickupFilters.city;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_CITY', val);
                }
            },
            state: {
                get() {
                    return this.$store.state.pickupFilters.state;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_STATE', val);
                }
            },
            zipCode: {
                get() {
                    return this.$store.state.pickupFilters.zipCode;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_ZIP_CODE', val);
                }
            },
            pickupDate: {
                get() {
                    return this.$store.state.pickupFilters.date;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_DATE', val);
                }
            },
            pickupDates: {
                get() {
                    return this.$store.state.pickupFilters.dateOptions;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_DATE_OPTIONS', val);
                }
            },
            pickupTime: {
                get() {
                    return this.$store.state.pickupFilters.time;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_TIME', val);
                }
            },
            pickupTimes: {
                get() {
                    return this.$store.state.pickupFilters.timeOptions;
                },
                set(val) {
                    this.$store.commit('SET_PICKUP_FILTER_TIME_OPTIONS', val);
                }
            },
            selectedCategories: {
                get() {
                    return this.$store.state.filters.selectedCategories;
                },
                set(val) {
                    this.$store.commit('SET_FILTER_SELECTED_CATEGORIES', val);
                }
            },
            selectedAttributes: {
                get() {
                    return this.$store.state.filters.selectedAttributes;
                },
                set(val) {
                    this.$store.commit('SET_FILTER_SELECTED_ATTRIBUTES', val);
                }
            }
        },
        watch: {
            pickupDate(n) {
                this.flowStrategy && this.flowStrategy.dateChanged(n);
            },
            pickupLocation(n) {
                this.flowStrategy && this.flowStrategy.locationChanged(n);
            }
        },
        data: () => ({
            errorDialog: false,
            errorText: "",
            locations: [],
            totalLocations: 0,
            currentDistributor: null,
            moreInfoModal: false,
            needPickupLocation: false,
            showPickupSelector: true,
            showDateInput: false,
            showTimeInput: false,
            mileOptions: [
                {val: 1, label: "< 1 mile"},
                {val: 5, label: "1-5 miles"},
                {val: 9999, label: "5+ miles"},
            ],
            proximityFilterEditable: false,
            statesOptions: StatesService.getStates(),
            flowStrategy: null
        }),
        mounted() {
            window.scrollTo(0, 0);
            this.startOver();
            this.loadPickupLocations().then(
            (results) => {
                this.pickupLocations = results.data
                if (this.pickupLocations.length > 0) {
                    this.needPickupLocation = true
                    this.flowStrategy = new ParentPickupLocationFlow(this);
                } else {
                    this.flowStrategy = new ChildrenPickupLocationFlow(this);
                }
            },
            (error) => {
                if (error.response.status == 403) {
                    this.$router.push("/login")
                }
                this.errorText = error.response.data
                this.errorDialog = true
            }).then(() => {
                this.$store.dispatch("setExecuting", false)
            })
        },
        methods: {
            buildCommonQueryParams() {
                let searchParams = new URLSearchParams();

                if(this.proximityMode === 'miles' && this.miles)
                    searchParams.append('miles', this.miles);

                if(this.proximityMode === 'cityState') {
                    if(this.city)
                        searchParams.append('city', this.city.trim());

                    if(this.state)
                        searchParams.append('state', this.state);
                }

                if(this.proximityMode === 'zip' && this.zipCode)
                    searchParams.append('zipCode', this.zipCode);

                if(this.pickupLocation)
                    searchParams.append('pickupLocation', this.pickupLocation);

                if(this.pickupDate)
                    searchParams.append('pickupDate', moment.utc(this.pickupDate).format('YYYY-MM-DD'));

                if(this.pickupTime)
                    searchParams.append('pickupTime', moment.utc(this.pickupTime).format('HH:mm'));

                return searchParams;
            },
            loadPickupLocations() {
                return api.get("/consumer/listPickupLocations", {params: this.buildCommonQueryParams()});
            },
            distanceLabel(d) {
                if (d <= 1) return "mile"
                else return "miles"
            },
            clearResults() {
                this.pickupLocations = [];
                this.pickupDates = [];
                this.pickupTimes = [];
                this.pickupLocation = null;
                this.vendor = null;
                this.pickupDate = null;
                this.pickupTime = null;
                this.needPickupLocation = false;
                this.showDateInput = false;
                this.showTimeInput = false;
            },
            startOver() {
                this.miles = 9999;
                this.proximityMode = 'miles';
                this.clearResults();
            },
            selectLocation(dist) {
                this.vendor = dist;

                if(!this.needPickupLocation)
                {
                    this.pickupLocation = dist;
                }

                this.$router.push("/shop/" + dist)
            },
            getPickupDates() {
                this.$store.dispatch("setExecuting", true);
                api.get('/consumer/startPickupDates', {params: this.buildCommonQueryParams()}).then(
                (results) => {
                    this.pickupDates = results.data
                },
                (error) => {
                    if (error.response.status == 403) {
                        this.$router.push("/login")
                    }
                    this.errorText = error.response.data
                    this.errorDialog = true
                }).then(() => {
                    this.showDateInput = true;
                    this.$store.dispatch("setExecuting", false)
                })
            },
            loadLocations(offset) {
                this.$store.dispatch("setExecuting", true);
                let url = "/consumer/listLocations";
                let searchParams = this.buildCommonQueryParams();
                searchParams.append('offset', offset);

                this.selectedCategories.forEach((catId) => searchParams.append('selectedCategories', catId));
                this.selectedAttributes.forEach((attrId) => searchParams.append('selectedAttributes', attrId));

                if (!this.loggedIn && this.selectedProgram) {
                    searchParams.append('guide', this.selectedProgram.id);
                }

                api.get(url, {params: searchParams}).then(
                (results) => {
                    this.locations = results.data.locations;
                    this.totalLocations = results.data.totalCount;
                },
                (error) => {
                    if (error.response.status == 403) {
                        this.$router.push("/login")
                    }
                    this.errorText = error.response.data
                    this.errorDialog = true
                }).then(() => {
                    this.$store.dispatch("setExecuting", false)
                })
            },
            locationSelector() {
                this.showPickupSelector = false;
                this.selectedCategories = [];
                this.selectedAttributes = [];
                this.loadLocations(0);
            },
            moreInfo(dist) {
                this.currentDistributor = dist;
                this.moreInfoModal = true;
            },
            applyFilters() {
                this.loadLocations(0);
            },
            applyProximityFilters() {
                this.proximityFilterEditable = false;
                this.flowStrategy.applyProximityFilters();
            },
            editProximityFilters() {
                this.proximityFilterEditable = true;
                this.clearResults();
            }
        }
    }
</script>
<style scoped>

    .logo-container {
        height: 150px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        border-radius: 5px;
        margin: 5px 0;
    }

    .theme--light.v-btn.v-btn--disabled {
        color: rgba(0, 0, 0, 0.5) !important;
    }

</style>
